import { EditRecipeLite, EditStepLite } from '@shared/models/recipe/recipe-lite.model';
import {
  RecipeTranslationVariant,
  VariantTranslation,
  VariantTranslationIngredients,
} from '@shared/models/recipe/recipes-translation-form.model';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { IngredientLite } from '@shared/models/ingredient/ingredient-lite.model';

export enum IngredientType {
  noType = 'noType',
}

export const mapRecipeLiteToVariantTranslations = (
  recipes: EditRecipeLite[],
  canHaveShortDescription: boolean,
  options?: {
    sourceIngredients?: IngredientLite[][];
    disabled?: boolean;
  }
): RecipeTranslationVariant[] => {
  const { title, shortTitle, summary, tips } = recipes[0];
  const titlesFg = new FormGroup({
    title: new FormControl(
      { value: title, disabled: !!options?.disabled },
      {
        nonNullable: true,
        validators: [Validators.required],
      }
    ),
    shortTitle: new FormControl({ value: shortTitle, disabled: !!options?.disabled }, { nonNullable: true }),
    summary: new FormControl({ value: summary, disabled: !!options?.disabled }, { nonNullable: true }),
  });
  const tipsControl = new FormControl({ value: tips, disabled: !!options?.disabled }, { nonNullable: true });

  return recipes.map((recipe, index) => {
    const sourceIngredients = options?.sourceIngredients?.[index];
    const ingredients: IngredientLite[] = sourceIngredients
      ? assignTypesFromSourceByIndex(sourceIngredients, recipe.ingredients)
      : recipe.ingredients;

    return {
      form: new FormGroup({
        titles: titlesFg,
        ingredients: mapIngredients(ingredients, options?.disabled),
        steps: mapSteps(recipe.steps ?? [], canHaveShortDescription, options?.disabled),
        tips: tipsControl,
      }),
      functionalId: recipe.identifier.functionalId,
      quantity: recipe.yield.quantity,
    };
  });
};

const assignTypesFromSourceByIndex = (sourceIngredients: IngredientLite[], targetIngredients: IngredientLite[]): IngredientLite[] =>
  targetIngredients.map((target, index) => ({
    ...target,
    type: target.type ?? sourceIngredients[index].type,
  }));

const mapIngredients = (ingredients: IngredientLite[], isDisabled?: boolean): VariantTranslation['ingredients'] => {
  const ingredientsByTypesFg = new FormGroup<VariantTranslationIngredients>({});

  if (!ingredients || ingredients.length === 0) {
    return ingredientsByTypesFg;
  }

  ingredients.forEach((ingredient) => {
    const normalizedType = ingredient.type ?? IngredientType.noType;
    const typeFg = ingredientsByTypesFg.controls[normalizedType];
    const ingredientFg = new FormGroup({
      [ingredient.identifier]: new FormGroup({
        description: new FormControl(
          {
            value: ingredient.applicationDescription,
            disabled: !!isDisabled,
          },
          { nonNullable: true, validators: [Validators.required] }
        ),
        ingredient: new FormControl(
          {
            value: ingredient.applianceDescription,
            disabled: !!isDisabled,
          },
          {
            nonNullable: true,
            validators: [Validators.required],
          }
        ),
      }),
    });

    if (typeFg) {
      typeFg.push(ingredientFg);
    } else {
      ingredientsByTypesFg.addControl(normalizedType, new FormArray([ingredientFg]));
    }
  });

  return ingredientsByTypesFg;
};

const mapSteps = (steps: EditStepLite[], canHaveShortDescription: boolean, isDisabled?: boolean): VariantTranslation['steps'] => {
  const stepsControls = steps.map((step) => {
    const shortDescriptionControl = new FormControl(
      {
        value: step.applianceDescription ?? '',
        disabled: !!isDisabled,
      },
      { nonNullable: true }
    );

    if (step.applianceDescription) {
      shortDescriptionControl.addValidators(Validators.required);
    }

    return new FormGroup({
      description: new FormControl(
        {
          value: step.applicationDescription,
          disabled: !!isDisabled,
        },
        { nonNullable: true, validators: [Validators.required] }
      ),
      ...(canHaveShortDescription && {
        shortDescription: shortDescriptionControl,
      }),
    });
  });

  return new FormArray(stepsControls);
};

export const mapVariantTranslationToEditRecipeLite = (
  original: EditRecipeLite,
  variant: RecipeTranslationVariant,
  isReady: boolean
): EditRecipeLite => {
  const translatedIngredients = mapTranslatedIngredients(original.ingredients, variant);
  const translatedSteps = original.steps !== undefined ? mapTranslatedSteps(original.steps, variant) : [];

  return {
    ...original,
    ingredients: translatedIngredients,
    steps: translatedSteps,
    fullIngredients: translatedIngredients.map((i) => i.applicationDescription).join('\n\n'),
    tips: variant.form.controls.tips.value,
    title: variant.form.controls.titles.value.title ?? original.title,
    shortTitle: variant.form.controls.titles.value.shortTitle ?? original.shortTitle,
    summary: variant.form.controls.titles.value.summary ?? original.summary,
    ...(isReady && {
      status: 'READY',
    }),
  };
};

export const mapTranslatedIngredients = (originalIngredients: IngredientLite[], variant: RecipeTranslationVariant): IngredientLite[] => {
  if (!originalIngredients) {
    return [];
  }

  const ingredientsForm = variant.form.controls.ingredients;
  return originalIngredients.map((originalIng) => {
    const allIngredientsFormsByOriginalFid: Record<
      string,
      {
        description: string;
        ingredient: string;
      }
    >[] = Object.values(ingredientsForm.controls).reduce(
      (acc: Record<string, { description: string; ingredient: string }>[], formArray) => {
        const entries = (formArray as FormArray).controls.map((formGroup) => (formGroup as FormGroup).value);
        return acc.concat(entries);
      },
      []
    );

    const translatedEntry = allIngredientsFormsByOriginalFid.find((obj) =>
      Object.prototype.hasOwnProperty.call(obj, originalIng.identifier)
    );

    const translated = translatedEntry?.[originalIng.identifier];
    return {
      ...originalIng,
      applicationDescription: translated?.description ?? originalIng.applicationDescription,
      applianceDescription: translated?.ingredient ?? originalIng.applianceDescription,
      endFreeText: translated?.ingredient ?? originalIng.applianceDescription,
    };
  });
};

export const mapTranslatedSteps = (originalSteps: EditStepLite[], variant: RecipeTranslationVariant): EditStepLite[] => {
  const stepsFormArray = variant.form.controls.steps;

  return originalSteps.map((originalStep, index) => {
    const applicationDescription = stepsFormArray.at(index).controls.description.getRawValue();
    const applianceDescription = stepsFormArray.at(index).controls.shortDescription?.getRawValue();

    return {
      ...originalStep,
      applicationDescription: applicationDescription ?? originalStep.applicationDescription,
      applianceDescription: applianceDescription ?? originalStep.applianceDescription,
    };
  });
};
